import { useMutation, useQuery } from '@tanstack/react-query';

import companyValueService from './companyValueService';

import type {
    CompanyValue,
    CreateCompanyValueRequest,
    UpdateCompanyValueRequest,
} from '@/types/company-values';

import { queryClient } from '@/AppWithProviders';
import { CompanyValueMutationKey, CompanyValueQueryKey } from '@/constants/QueryKeys';

export const useGetCompanyValues = () => {
    return useQuery<CompanyValue[]>({
        queryKey: [CompanyValueQueryKey.COMPANY_VALUES],
        queryFn: () => companyValueService.getCompanyValues(),
    });
};

export const useGetCompanyValueById = (id: string) => {
    return useQuery<CompanyValue>({
        queryKey: [CompanyValueQueryKey.COMPANY_VALUE, id],
        queryFn: () => companyValueService.getCompanyValueById(id),
        enabled: !!id,
    });
};

export const useCreateCompanyValue = () => {
    return useMutation<CompanyValue, Error, CreateCompanyValueRequest>({
        mutationKey: [CompanyValueMutationKey.CREATE_COMPANY_VALUE],
        mutationFn: (request) => companyValueService.createCompanyValue(request),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [CompanyValueQueryKey.COMPANY_VALUES],
            });
        },
    });
};

export const useUpdateCompanyValue = () => {
    return useMutation<CompanyValue, Error, { id: string; request: UpdateCompanyValueRequest }>({
        mutationKey: [CompanyValueMutationKey.UPDATE_COMPANY_VALUE],
        mutationFn: ({ id, request }) => companyValueService.updateCompanyValue(id, request),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [CompanyValueQueryKey.COMPANY_VALUES],
            });
            queryClient.invalidateQueries({
                queryKey: [CompanyValueQueryKey.COMPANY_VALUE],
            });
        },
    });
};

export const useDeleteCompanyValue = () => {
    return useMutation<CompanyValue, Error, string>({
        mutationKey: [CompanyValueMutationKey.DELETE_COMPANY_VALUE],
        mutationFn: (id: string) => companyValueService.deleteCompanyValue(id),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [CompanyValueQueryKey.COMPANY_VALUES],
            });
        },
    });
};

export const useToggleCompanyValueStatus = () => {
    return useMutation<CompanyValue, Error, string>({
        mutationKey: [CompanyValueMutationKey.TOGGLE_STATUS],
        mutationFn: (id: string) => companyValueService.toggleCompanyValueStatus(id),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [CompanyValueQueryKey.COMPANY_VALUES],
            });
        },
    });
};

export const useGetCompanyValueImpactCounts = (id: string, enabled: boolean) => {
    return useQuery({
        queryKey: [CompanyValueQueryKey.COMPANY_VALUE_IMPACT, id],
        queryFn: () => companyValueService.getCompanyValueImpactCounts(id),
        enabled: enabled && !!id,
        staleTime: 0,
        gcTime: 0,
    });
};
